import React, { useEffect, useState } from "react";
import { useStateContext } from "../../contexts/ContextProvider";
import { userRequest } from "../../utils/requestMethods";
import { useHistory } from "react-router-dom";
import SkeletonTable from "../../components/common/SkeletonTable";
import AlertNotification from "../../components/common/AlertNotification";
import DataTable from "react-data-table-component";
import { BackButton } from "../../components";
import { FiSave, FiSend, FiFileText, FiAlertTriangle, FiMessageSquare } from "react-icons/fi";
import { generateSchoolSessions } from "../../utils/schoolYearUtils";

const TERM_LABELS = { 1: "First Term", 2: "Second Term", 3: "Third Term" };
const schoolSessions = generateSchoolSessions(2023, 10);

const UploadClassResult = () => {
  const { school } = useStateContext();
  const history = useHistory();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [results, setResults] = useState([]); // [{ childId, cas: [{ca, score}], exam, total }]
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState("");
  const [publishError, setPublishError] = useState("");
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPublishClassModal, setShowPublishClassModal] = useState(false);
  const [publishClassLoading, setPublishClassLoading] = useState(false);
  const [publishClassSuccess, setPublishClassSuccess] = useState("");
  const [publishClassError, setPublishClassError] = useState("");

  // Get assessment system based on selected class's level of education
  const getAssessmentSystemForClass = (classObj) => {
    if (!classObj?.level_of_education || !school?.settings?.assessment?.assessment_system) {
      // No assessment system available
      return { ca: { cas: [] }, exam: { max_score: 100 } };
    }

    const levelOfEducation = classObj.level_of_education;

    // Find the assessment system for this level of education
    const assessmentConfig = school.settings.assessment.assessment_system.find(
      (config) => config.level_of_education === levelOfEducation,
    );

    // If found, use it; otherwise, use the default one
    if (assessmentConfig) {
      return assessmentConfig;
    }

    // Fallback to default assessment system
    const defaultConfig = school.settings.assessment.assessment_system.find(
      (config) => config.is_default === true,
    );

    return defaultConfig || { ca: { cas: [] }, exam: { max_score: 100 } };
  };

  // Get CA settings and exam max score based on selected class
  const currentAssessmentSystem = selectedClass
    ? getAssessmentSystemForClass(selectedClass)
    : { ca: { cas: [] }, exam: { max_score: 100 } };
  const caSettings = currentAssessmentSystem?.ca?.cas || [];
  const examMaxScore = currentAssessmentSystem?.exam?.max_score || 100;
  const currentTerm = school?.settings?.current_term;
  const currentSession = school?.settings?.current_session;
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (!currentTerm || !currentSession) return;
    setSelectedTerm((prev) =>
      prev !== null ? prev : Number(localStorage.getItem("uploadResult_term")) || currentTerm,
    );
    setSelectedSession((prev) =>
      prev !== null ? prev : localStorage.getItem("uploadResult_session") || currentSession,
    );
  }, [currentTerm, currentSession]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!school) return;
      setLoading(true);
      try {
        const { data } = await userRequest.get(`classes?code=${school?.code}`);
        setClasses(data.classes || []);
      } catch (e) {
        setError("Failed to fetch classes");
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, [school]);

  // Fetch subjects for selected class
  useEffect(() => {
    if (!selectedClass || !school?._id) {
      setSubjects([]);
      return;
    }
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const { data } = await userRequest.get(
          `/subjects/${school?._id}/${selectedClass?._id}?limit=50`,
        );
        setSubjects(data.subjects || []);
      } catch (e) {
        setError("Failed to fetch subjects");
      } finally {
        setLoading(false);
      }
    };
    fetchSubjects();
  }, [selectedClass, school?._id]);

  // Fetch students when class changes
  useEffect(() => {
    if (!selectedClass) {
      setStudents([]);
      setResults([]);
      return;
    }
    setStudentLoading(true);
    const fetchStudents = async () => {
      try {
        const { data } = await userRequest.get(`children/class/${selectedClass?._id}?limit=200`);
        setStudents(data.children || []);
        if (!selectedSubject) {
          setResults([]); // Clear table if no subject is selected
        }
      } catch (e) {
        setError("Failed to fetch students");
        setStudents([]);
        setResults([]);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudents();
  }, [selectedClass, selectedSubject]);

  // Fetch results when subject changes and students are loaded
  useEffect(() => {
    const fetchClassResult = async () => {
      if (
        !selectedClass ||
        !selectedSubject ||
        !students.length ||
        !selectedTerm ||
        !selectedSession
      )
        return;
      setLoading(true);
      try {
        const { data } = await userRequest.get(
          `/result/${school._id}?class_id=${selectedClass?._id}&subject_id=${selectedSubject?._id}&term=${selectedTerm}&session=${selectedSession}&limit=100`,
        );
        setResults(
          students.map((student) => {
            const result = data.data.find((r) => r.child._id === student._id);
            if (result && result.results && result.results[0]) {
              return {
                childId: student._id,
                cas: caSettings.map((ca, idx) => {
                  const caObj = result.results[0].cas.find((c) => c.ca === idx + 1);
                  return { ca: idx + 1, score: caObj ? caObj.score : 0 };
                }),
                exam: result.results[0].exam || 0,
                total: result.results[0].total || 0,
                firstname: student.firstname,
                lastname: student.lastname,
              };
            } else {
              return {
                childId: student._id,
                cas: caSettings.map((ca, idx) => ({ ca: idx + 1, score: 0 })),
                exam: 0,
                total: 0,
                firstname: student.firstname,
                lastname: student.lastname,
              };
            }
          }),
        );
      } catch (e) {
        setResults(
          students.map((student) => ({
            childId: student._id,
            cas: caSettings.map((ca, idx) => ({ ca: idx + 1, score: 0 })),
            exam: 0,
            total: 0,
            firstname: student.firstname,
            lastname: student.lastname,
          })),
        );
      } finally {
        setLoading(false);
      }
    };
    if (selectedClass && selectedSubject && students.length) {
      fetchClassResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSubject, students, school?._id, selectedTerm, selectedSession]);

  // Handle CA/exam input change
  const handleInputChange = (studentIdx, type, value, caIdx = null) => {
    setResults((prev) => {
      const updated = [...prev];
      if (type === "ca") {
        updated[studentIdx].cas[caIdx].score = Number(value);
      } else if (type === "exam") {
        updated[studentIdx].exam = Number(value);
      }
      // Update total
      const caSum = updated[studentIdx].cas.reduce((sum, ca) => sum + (Number(ca.score) || 0), 0);
      updated[studentIdx].total = caSum + (Number(updated[studentIdx].exam) || 0);
      return updated;
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedClass || !selectedSubject) {
      setError("Please select a class and subject.");
      return;
    }
    // Validate all scores are numbers and within max_score
    for (let i = 0; i < results.length; i++) {
      for (let j = 0; j < caSettings.length; j++) {
        const score = results[i].cas[j].score;
        if (score < 0 || score > caSettings[j].max_score) {
          setError(
            `CA ${caSettings[j].title || j + 1} for ${results[i].firstname} ${results[i].lastname} must be between 0 and ${caSettings[j].max_score}`,
          );
          return;
        }
      }
      // Exam validation using the new assessment system
      if (results[i].exam < 0 || results[i].exam > examMaxScore) {
        setError(
          `Exam for ${results[i].firstname} ${results[i].lastname} must be between 0 and ${examMaxScore}`,
        );
        return;
      }
    }
    setLoading(true);
    try {
      await userRequest.post("/result/multiple-result", {
        subjectId: selectedSubject._id,
        schoolId: school._id,
        session: selectedSession,
        term: selectedTerm,
        classId: selectedClass._id,
        results: results.map(({ childId, cas, exam }) => ({ childId, cas, exam })),
      });
      setSuccess("Results uploaded successfully!");
    } catch (e) {
      setError("Failed to upload results");
    } finally {
      setLoading(false);
    }
  };

  // Handle publish
  const handlePublish = () => {
    if (!selectedClass || !selectedSubject) {
      setPublishError("Please select a class and subject to publish.");
      return;
    }
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    setPublishLoading(true);
    setPublishError("");
    setPublishSuccess("");
    try {
      await userRequest.post(`/result/publish/${school?._id}/${selectedSubject?._id}`, {
        term: selectedTerm,
        session: selectedSession,
        classId: selectedClass?._id,
      });
      setPublishSuccess("Result published successfully!");
    } catch (e) {
      setPublishError("Failed to publish result.");
    } finally {
      setPublishLoading(false);
      setShowPublishModal(false);
    }
  };

  const handlePublishClass = () => {
    if (!selectedClass) {
      setPublishClassError("Please select a class to publish.");
      return;
    }
    setShowPublishClassModal(true);
  };

  const confirmPublishClass = async () => {
    setPublishClassLoading(true);
    setPublishClassError("");
    setPublishClassSuccess("");
    try {
      await userRequest.post(`/result/publish-class/${school?._id}/${selectedClass?._id}`, {
        term: selectedTerm,
        session: selectedSession,
      });
      setPublishClassSuccess("Class results published successfully!");
    } catch (e) {
      setPublishClassError("Failed to publish class results.");
    } finally {
      setPublishClassLoading(false);
      setShowPublishClassModal(false);
    }
  };

  // DataTable columns for uploading results
  const columns = [
    {
      name: "First Name",
      selector: (row) => row.firstname,
      sortable: true,
      //width: "140px"
    },
    {
      name: "Last Name",
      selector: (row) => row.lastname,
      sortable: true,
      //width: "140px"
    },
    ...caSettings.map((ca, caIdx) => ({
      name: ca.title ? `${ca.title} (${ca.max_score})` : `CA ${caIdx + 1} (${ca.max_score})`,
      cell: (row, studentIdx) => (
        <input
          type="number"
          min={0}
          max={ca.max_score}
          value={row.cas[caIdx].score}
          onChange={(e) =>
            handleInputChange(
              results.findIndex((s) => s.childId === row.childId),
              "ca",
              e.target.value,
              caIdx,
            )
          }
          className="w-16 border rounded-lg px-1 py-1 text-center focus:ring-blue-500 focus:border-blue-500"
        />
      ),
      //width: "120px"
    })),
    {
      name: `Exam (${examMaxScore})`,
      cell: (row, studentIdx) => (
        <input
          type="number"
          min={0}
          max={examMaxScore}
          value={row.exam}
          onChange={(e) =>
            handleInputChange(
              results.findIndex((s) => s.childId === row.childId),
              "exam",
              e.target.value,
            )
          }
          className="w-16 border rounded-lg px-1 py-1 text-center focus:ring-blue-500 focus:border-blue-500"
        />
      ),
      //width: "120px"
    },
    {
      name: "Total",
      selector: (row) => row.total,
      width: "90px",
      cell: (row) => <span className="font-bold">{row.total}</span>,
    },
    {
      name: "Remarks",
      width: "110px",
      cell: (row) => (
        <button
          type="button"
          title="Add remarks & comments"
          onClick={() =>
            history.push(`/students/view/${row.childId}/${selectedClass?._id}`, { tab: "result" })
          }
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
        >
          Remarks
        </button>
      ),
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#F8FAFC",
        borderBottom: "1px solid #E2E8F0",
      },
    },
    headCells: {
      style: {
        fontSize: "11px",
        fontWeight: "700",
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        borderBottom: "1px solid #F1F5F9",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#F5F9FF",
        borderBottomColor: "#E0E7FF",
      },
    },
    cells: {
      style: {
        paddingTop: "10px",
        paddingBottom: "10px",
      },
    },
  };

  return (
    <div className="m-2 mt-24 md:mx-10 md:my-0 py-2 md:py-7">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-10 min-h-screen">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-1">
          <BackButton history={history} />
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest">
              Result
            </p>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Upload Class Result</h1>
          </div>
        </div>
        {selectedTerm && selectedSession && (
          <p className="text-sm text-gray-400 mb-6 pl-[52px]">
            {TERM_LABELS[selectedTerm]} &middot; {selectedSession}
          </p>
        )}

        {/* Notifications */}
        <div className="space-y-2 mb-6">
          {error && <AlertNotification type="error" message={error} onClose={() => setError("")} />}
          {success && (
            <AlertNotification type="success" message={success} onClose={() => setSuccess("")} />
          )}
          {publishError && (
            <AlertNotification
              type="error"
              message={publishError}
              onClose={() => setPublishError("")}
            />
          )}
          {publishSuccess && (
            <AlertNotification
              type="success"
              message={publishSuccess}
              onClose={() => setPublishSuccess("")}
            />
          )}
          {publishClassError && (
            <AlertNotification
              type="error"
              message={publishClassError}
              onClose={() => setPublishClassError("")}
            />
          )}
          {publishClassSuccess && (
            <AlertNotification
              type="success"
              message={publishClassSuccess}
              onClose={() => setPublishClassSuccess("")}
            />
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Filter card */}
          <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filters</p>
            </div>
            <div className="p-5 flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Term
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedTerm || ""}
                  onChange={(e) => {
                    const t = Number(e.target.value);
                    setSelectedTerm(t);
                    localStorage.setItem("uploadResult_term", t);
                  }}
                >
                  {[1, 2, 3].map((t) => (
                    <option key={t} value={t}>
                      {TERM_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Session
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedSession || ""}
                  onChange={(e) => {
                    setSelectedSession(e.target.value);
                    localStorage.setItem("uploadResult_session", e.target.value);
                  }}
                >
                  {schoolSessions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Class
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5"
                  value={selectedClass?._id || ""}
                  onChange={(e) => {
                    const cl = classes.find((c) => c._id === e.target.value);
                    setSelectedClass(cl || null);
                    setSelectedSubject(null);
                    setStudents([]);
                    setResults([]);
                  }}
                >
                  <option value="">Select class</option>
                  {classes.map((cl) => (
                    <option key={cl._id} value={cl._id}>
                      {cl.class_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[130px]">
                <label className="block mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Subject
                </label>
                <select
                  className="bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-3 py-2.5 disabled:bg-gray-50 disabled:text-gray-400"
                  value={selectedSubject?._id || ""}
                  onChange={(e) => {
                    const subj = subjects.find((s) => s._id === e.target.value);
                    setSelectedSubject(subj || null);
                  }}
                  disabled={!selectedClass}
                >
                  <option value="">Select subject</option>
                  {subjects.map((subj) => (
                    <option key={subj._id} value={subj._id}>
                      {subj.subject_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results table card */}
          <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2">
              {/* Context info */}
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                {selectedClass && selectedSubject ? (
                  <>
                    <span className="text-sm font-semibold text-gray-700 truncate">
                      {selectedClass.class_name}
                    </span>
                    <span className="text-gray-300">&middot;</span>
                    <span className="text-sm text-gray-500 truncate">
                      {selectedSubject.subject_name}
                    </span>
                    {results.length > 0 && (
                      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                        {results.length} students
                      </span>
                    )}
                  </>
                ) : (
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Results
                  </p>
                )}
              </div>
              {/* Action buttons */}
              <div className="flex gap-2 sm:ml-auto flex-shrink-0">
                <button
                  type="submit"
                  disabled={loading || !selectedClass || !selectedSubject}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-white text-xs bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] hover:from-[#2a2a7a] hover:to-[#2a5bb8] hover:shadow-md hover:scale-[1.02] transition-all duration-200 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-3 h-3" /> Save Results
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={publishLoading || !selectedClass || !selectedSubject}
                  onClick={handlePublish}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-xs text-[#3F3DA5] bg-[#EEEEFC] border border-[#C5C5E8] hover:bg-indigo-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {publishLoading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-[#3F3DA5] border-t-transparent rounded-full animate-spin" />{" "}
                      Publishing...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-3 h-3" /> Publish
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={publishClassLoading || !selectedClass}
                  onClick={handlePublishClass}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {publishClassLoading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />{" "}
                      Publishing...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-3 h-3" /> Publish Class
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3F3DA5]" />
                    <span className="text-sm text-[#3F3DA5] font-medium">Loading...</span>
                  </div>
                </div>
              )}
              <DataTable
                columns={columns}
                data={results}
                noDataComponent={
                  <div className="flex flex-col items-center py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                      <FiFileText className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-500">
                      {!selectedClass
                        ? "Select a class to get started"
                        : !selectedSubject
                          ? "Select a subject to load results"
                          : "No students found in this class"}
                    </p>
                  </div>
                }
                customStyles={customStyles}
                progressPending={studentLoading}
                progressComponent={<SkeletonTable rows={5} columns={caSettings.length + 5} />}
                highlightOnHover
              />
            </div>
          </div>
        </form>

        {/* Publish confirmation modal */}
        {showPublishModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
                  <FiAlertTriangle className="w-7 h-7 text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Publish Results?</h2>
                <p className="text-sm text-gray-500 mb-1">
                  You're about to publish results for{" "}
                  <span className="font-semibold text-gray-700">
                    {selectedSubject?.subject_name}
                  </span>{" "}
                  in{" "}
                  <span className="font-semibold text-gray-700">{selectedClass?.class_name}</span>.
                </p>
                <p className="text-xs text-red-500 font-medium mb-6">
                  This action cannot be undone.
                </p>
                <div className="flex w-full gap-3">
                  <button
                    onClick={() => setShowPublishModal(false)}
                    disabled={publishLoading}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPublish}
                    disabled={publishLoading}
                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] text-white font-semibold text-sm hover:shadow-md transition-all disabled:opacity-60"
                  >
                    {publishLoading ? "Publishing..." : "Confirm Publish"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Publish Class confirmation modal */}
        {showPublishClassModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                  <FiAlertTriangle className="w-7 h-7 text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Publish Class Results?</h2>
                <p className="text-sm text-gray-500 mb-1">
                  You're about to publish all results for{" "}
                  <span className="font-semibold text-gray-700">{selectedClass?.class_name}</span>.
                </p>
                <p className="text-xs text-red-500 font-medium mb-6">
                  This will publish results for all subjects in this class. This action cannot be
                  undone.
                </p>
                <div className="flex w-full gap-3">
                  <button
                    onClick={() => setShowPublishClassModal(false)}
                    disabled={publishClassLoading}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPublishClass}
                    disabled={publishClassLoading}
                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold text-sm hover:shadow-md transition-all disabled:opacity-60"
                  >
                    {publishClassLoading ? "Publishing..." : "Confirm Publish"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadClassResult;
